import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ArticleEnums } from 'src/app/enum/article-enums';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { DocumentSupportDetailModel } from './../../../../models/shopping/document-support-detail.model';
import { DocumentSupportModel } from './../../../../models/shopping/document-support.model';
import { DocumentSupportDetailService } from './../../../../services/shopping/document-support-detail.service';

@Component({
  selector: 'app-register-document-support-detail',
  templateUrl: './register-document-support-detail.component.html',
  styleUrls: ['./register-document-support-detail.component.scss'],
  standalone: false
})
export class RegisterDocumentSupportDetailComponent implements OnInit {

  @Input() documentSupportModel: DocumentSupportModel = new DocumentSupportModel();
  @Input() documentSupportDetailContract: DocumentSupportDetailModel = new DocumentSupportDetailModel();
  @Input() disabled: boolean = false;
  @Output() newDocumentSupportDetail = new EventEmitter<DocumentSupportDetailModel>();
  @Output() updateListDetail = new EventEmitter<boolean>();
  @Output() isFormView = new EventEmitter<boolean>();

  articleId: number = 0;
  articleType: number = ArticleEnums.facturas;
  codeNameArticle: string = '';
  disableButtonSave: boolean = false;
  documentSupportDetailId: number = 0;
  formDocumentSupportDetail: FormGroup = new FormGroup({});

  constructor(
    private articleService: ArticleService,
    private documentSupportDetailService: DocumentSupportDetailService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService) {
  }

  async ngOnInit() {
    this.validateForm(new DocumentSupportDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const documentSupportDetailContract = changes["documentSupportDetailContract"];
    if (documentSupportDetailContract && !documentSupportDetailContract.firstChange) {
      if (this.documentSupportDetailContract.documentSupportDetailId == 0) {
        this.createNewDocumentSupportDetail(documentSupportDetailContract.currentValue);
      } else {
        this.documentSupportDetailId = this.documentSupportDetailContract.documentSupportDetailId;
        await this.validateForm(this.documentSupportDetailContract);
      }
    }

    if (this.disabled) setTimeout(() => this.formDocumentSupportDetail.disable(), 0);
    setTimeout(() => this.formDocumentSupportDetail.controls["totalValue"].disable(), 100);
  }

  calculateTotalValue() {
    const { quantity = 0, unitValue = 0 } = this.formDocumentSupportDetail.value;
    const totalValue = quantity * unitValue;
    this.formDocumentSupportDetail.controls["totalValue"].patchValue(totalValue);
  }

  clearForm() {
    this.articleId = 0;
    this.codeNameArticle = '';
    this.validateForm(new DocumentSupportDetailModel());
    this.isFormView.emit(false);
  }

  async createNewDocumentSupportDetail(documentSupportDetail: DocumentSupportDetailModel) {
    try {
      this.documentSupportDetailId = 0;
      this.validateForm(documentSupportDetail);
    } catch (error) {
      console.error('Error al crear el detalle del documento soporte.', error);
    }
  }

  async getArticle(articleId: number | null) {
    try {
      let validArticle = null;
      let codeNameArticle = "";
      if (articleId != null) {
        const article = await firstValueFrom(this.articleService.getArticleById(articleId));
        if (article) {
          validArticle = articleId;
          codeNameArticle = `${article.code} - ${article.name}`;
        }
      }

      this.articleId = validArticle ?? 0;
      this.codeNameArticle = codeNameArticle;
      this.formDocumentSupportDetail.controls["articleId"].patchValue(validArticle);
    } catch (error) {
      console.error('Error al obtener el artículo:', error);
      this.articleId = 0;
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView.emit(isFormView);
  }

  async send() {
    if (this.formDocumentSupportDetail.valid) {
      const modelDocumentSupportDetail = this.setForm();
      try {
        if (this.documentSupportDetailId == 0) {
          const response = await firstValueFrom(this.documentSupportDetailService.createDocumentSupport(modelDocumentSupportDetail));
          this.documentSupportDetailId = response.documentSupportDetailId;
          this.toastHandlerService.showToast('success', 'El detalle fue creado exitosamente.', '');
        }
        else {
          const response = await firstValueFrom(this.documentSupportDetailService.updateDocumentSupportDetail(modelDocumentSupportDetail));
          this.documentSupportDetailId = response.documentSupportDetailId;
          this.toastHandlerService.showToast('success', 'El detalle fue modificado exitosamente.', '');
        }
        this.documentSupportDetailContract = new DocumentSupportDetailModel();
        this.clearForm();
        this.isFormView.emit(true);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al guardar el detalle.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  setForm(): any {
    this.documentSupportDetailContract.quantity = this.formDocumentSupportDetail.get("quantity")?.value;
    this.documentSupportDetailContract.unitValue = this.formDocumentSupportDetail.get("unitValue")?.value;
    this.documentSupportDetailContract.totalValue = this.formDocumentSupportDetail.get("totalValue")?.value;
    return this.documentSupportDetailContract;
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'El campo artículo es obligatorio.'
      },
      quantity: {
        required: 'El campo cantidad es obligatorio.',
        min: 'La cantidad mínima es 1.'
      },
      unitValue: {
        required: 'El campo Vr.Unitario es obligatorio.',
        min: 'El valor unitario mínimo es 1.'
      },
      totalValue: {
        required: 'El campo Vr.Total es obligatorio.',
        min: 'El valor total mínimo es 1.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formDocumentSupportDetail.get(controlName);

      if (control && control.invalid && control.errors) {
        const errors = control.errors;

        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async validateForm(documentSupportDetailModel: DocumentSupportDetailModel) {
    this.formDocumentSupportDetail = this.formBuilder.group({
      articleId: [documentSupportDetailModel.articleId, [Validators.required, Validators.min(1)]],
      quantity: [documentSupportDetailModel.quantity, [Validators.required, Validators.min(1)]],
      unitValue: [documentSupportDetailModel.unitValue, [Validators.required, Validators.min(1)]],
      totalValue: [documentSupportDetailModel.totalValue, [Validators.required, Validators.min(1)]]
    });

    if (documentSupportDetailModel.articleId != 0) {
      this.isFormView.emit(true);
      this.getArticle(documentSupportDetailModel.articleId);
    }

  }
}
